import _ from 'lodash';

const getObjectDiff = (object, base) => {
  const getChanges = (object, base) => {
    return _.transform(object, (result, value, key) => {
      if (!_.isEqual(value, base[key])) {
        //eslint-disable-next-line
        result[key] =
          _.isObject(value) && _.isObject(base[key])
            ? getChanges(value, base[key])
            : value;
      }
    });
  };
  return getChanges(object, base);
};

const formatProductToSubmit = (formData, initialFormData) => {
  const formDataCopy = _.cloneDeep(formData);
  const initialFormDataCopy = _.cloneDeep(initialFormData);
  const submitData = _.isEmpty(initialFormData)
    ? formDataCopy
    : getObjectDiff(formDataCopy, initialFormDataCopy);
  const { productCatalogVariants: submitVariants, defaultVariant } = submitData;
  const { productCatalogVariants: formVariants } = formDataCopy;

  //we need to format default variant as object with field sku
  if (defaultVariant) {
    const defaultSku = _.get(formVariants, [defaultVariant, 'sku']);
    submitData.defaultVariant = {
      sku: defaultSku,
    };
  }

  //we want to send array fields from form data because BE will overwrite with whatever values we send, so we can't send null values for items in the array that are not in the diff between initial form data and form data
  submitData.shippingRestrictions = formDataCopy.shippingRestrictions;

  //format variant descriptors
  _.forEach(submitVariants, (submitVariant, index) => {
    if (submitVariant) {
      const {
        id,
        legacyPostId,
        colorAttribute: formColorAttribute,
        colorAttribute2: formColorAttribute2,
        galleryUrls,
        privateImageUrls,
      } = formVariants[index];
      const {
        colorAttributeDescriptor,
        colorAttribute,
        colorAttribute2,
        wordAttributes,
        retailPrice,
        order,
      } = submitVariant;
      submitVariant.galleryUrls = galleryUrls;
      submitVariant.privateImageUrls = privateImageUrls;
      const variantDescriptors = [];
      if (_.size(wordAttributes)) {
        _.forEach(wordAttributes, (attribute, i) => {
          variantDescriptors.push({
            //for word attributes, the descriptor type is the order it is in the list
            type: `WORD${i + 1}`,
            descriptor: attribute,
          });
        });
      }
      const colorDescriptor = {};
      const hasColorAttributeDescriptor = !_.isNil(colorAttributeDescriptor);
      const hasColorAttribute = !_.isNil(colorAttribute);
      const hasColorAttribute2 = !_.isNil(colorAttribute2);
      if (hasColorAttributeDescriptor) {
        colorDescriptor.descriptor = colorAttributeDescriptor;
      }
      if (hasColorAttribute || hasColorAttribute2) {
        //BE will overwrite this value with whatever we send, so we want to send what's in the form if these were updated
        colorDescriptor.value = [formColorAttribute, formColorAttribute2];
      }
      if (!_.isEmpty(colorDescriptor)) {
        colorDescriptor.type = 'color';
        variantDescriptors.push(colorDescriptor);
      }
      if (_.size(variantDescriptors)) {
        submitVariant.variantDescriptors = variantDescriptors;
      }
      if (id) {
        submitVariant.id = id;
      }
      if (legacyPostId) {
        submitVariant.legacyPostId = legacyPostId;
      }
      if (retailPrice) {
        submitVariant.retailPrice = (retailPrice * 100).toFixed(0);
      }
      if (!order) submitVariant.order = index + 1;
      //delete form fields that are not needed to submit
      if (hasColorAttributeDescriptor) {
        delete submitVariant.colorAttributeDescriptor;
      }
      if (hasColorAttribute) {
        delete submitVariant.colorAttribute;
      }
      if (hasColorAttribute2) {
        delete submitVariant.colorAttribute2;
      }
      if (wordAttributes) {
        delete submitVariant.wordAttributes;
      }
    }
  });
  //delete form fields that are not needed to submit
  if (submitData.colorAttributeCount) {
    delete submitData.colorAttributeCount;
  }
  if (submitData.wordAttributesCount) {
    delete submitData.wordAttributesCount;
  }
  //filter out empty values
  submitData.productCatalogVariants = _.filter(
    submitData.productCatalogVariants,
    Boolean
  );
  return submitData;
};

export const formatProductToDisplay = (formData) => {
  const formattedData = _.cloneDeep(formData);
  const { defaultVariant, productCatalogVariants, taxonomyId } = formattedData;
  let wordAttributesCount = 0;

  const formattedVariants = _.map(
    _.sortBy(productCatalogVariants, (v) => v.order),
    (variant, index) => {
      let variantWordsAttributeCount = 0;
      const formattedVariant = _.cloneDeep(variant);
      const { variantDescriptors, retailPrice, sku } = formattedVariant;
      if (sku === _.get(defaultVariant, 'sku')) {
        formattedData.defaultVariant = _.toString(index);
      }
      formattedVariant.retailPrice = retailPrice
        ? (retailPrice / 100).toFixed(2)
        : '';
      formattedVariant.wordAttributes = [];
      _.forEach(
        //sort variants descriptors by type
        _.sortBy(variantDescriptors, (d) => d.type),
        (v) => {
          const { type, value, descriptor } = v;
          //format color descriptor
          if (type === 'COLOR') {
            formattedData.colorAttributeCount = '1';
            const [colorAttribute, colorAttribute2] = value;
            formattedVariant.colorAttribute = colorAttribute;
            //this field cannot be null, or will be saved in DB as "null"
            formattedVariant.colorAttribute2 = colorAttribute2 || '';
            formattedVariant.colorAttributeDescriptor = descriptor;
          } else {
            formattedVariant.wordAttributes.push(descriptor);
            //count number of word descriptors to display in form
            variantWordsAttributeCount += 1;
          }
        }
      );
      if (variantWordsAttributeCount > wordAttributesCount) {
        wordAttributesCount = variantWordsAttributeCount;
      }
      return formattedVariant;
    }
  );

  //we need to add empty strings for missing variant descriptors so that they will properly display in the form
  _.forEach(formattedVariants, (variant) => {
    for (let i = 0; i < wordAttributesCount; i += 1) {
      //assign empty string to any undefined values in the array
      if (_.isNil(variant.wordAttributes[i])) {
        variant.wordAttributes[i] = '';
      }
    }
    if (formattedData.colorAttributeCount === '1') {
      if (!variant.colorAttributeDescriptor)
        variant.colorAttributeDescriptor = '';
      if (!variant.colorAttribute) variant.colorAttribute = '';
      if (!variant.colorAttribute2) variant.colorAttribute2 = '';
    }
  });
  formattedData.taxonomyId = taxonomyId ? taxonomyId.toString() : '';
  //format word attributes count to string
  formattedData.wordAttributesCount = wordAttributesCount.toString();
  //if there are no variant descriptors, set default variant to empty string to match form dropdown value
  formattedData.defaultVariant =
    !formattedData.wordAttributesCount && !formattedData.colorAttributeCount
      ? ''
      : formattedData.defaultVariant;
  formattedData.productCatalogVariants = formattedVariants;
  return formattedData;
};

export const validateForm = (handleErrors, formData, setVariantErrors) => {
  const { defaultVariant, productCatalogVariants } = formData;
  let isValid = true;
  if (!defaultVariant) {
    isValid = false;
    handleErrors({
      defaultVariant: 'This field is required.',
    });
  }
  const defaultSku = _.get(productCatalogVariants, [defaultVariant, 'sku']);
  if (defaultVariant && _.isEmpty(defaultSku)) {
    isValid = false;
    handleErrors({
      defaultVariant: 'You must enter a SKU to select this variant.',
    });
  }
  _.forEach(productCatalogVariants, (variant, index) => {
    const { sku, colorAttribute, colorAttribute2, colorAttributeDescriptor } =
      variant;
    if (_.isEmpty(sku)) {
      isValid = false;
      setVariantErrors((prevState) => {
        const newState = _.cloneDeep(prevState);
        newState[index] = {
          ...prevState[index],
          sku: 'SKU is required.',
        };
        return newState;
      });
    }
    if ((colorAttribute || colorAttribute2) && !colorAttributeDescriptor) {
      isValid = false;
      setVariantErrors((prevState) => {
        const newState = _.cloneDeep(prevState);
        newState[index] = {
          ...prevState[index],
          colorAttributeDescriptor:
            'This field is required if there is a color value.',
        };
        return newState;
      });
    }
    if (colorAttributeDescriptor && !colorAttribute && !colorAttribute2) {
      isValid = false;
      setVariantErrors((prevState) => {
        const newState = _.cloneDeep(prevState);
        newState[index] = {
          ...prevState[index],
          colorAttribute:
            'This field is required if there is a color descriptor.',
        };
        return newState;
      });
    }
  });
  return isValid;
};

const formatMetadata = (dirtyFields, metadata) => {
  const formattedData = {};
  _.forOwn(dirtyFields, (value, id) => {
    //get form data
    formattedData[id] = [];
    _.forOwn(value, (v, fieldName) => {
      const formData = metadata[id][fieldName];
      const dataObj = {
        variantId: id,
        choiceType: fieldName,
      };
      if (_.isArray(formData)) {
        dataObj.choiceIds = formData;
        dataObj.choiceValue = '';
      } else {
        dataObj.choiceValue = formData;
        dataObj.choiceIds = [];
      }
      formattedData[id].push(dataObj);
    });
  });
  return formattedData;
};

export const formatSkuText = (variants, isSaved) => {
  const length = _.size(variants);
  const firstSku = _.get(variants, '[0].sku');
  const secondSku = _.get(variants, '[1].sku');
  const joinedSkus = _.join(
    _.map(variants, (v) => `"${v.sku}"`),
    ', '
  );

  if (isSaved) {
    if (length > 5) {
      return `Your Variable Product has been created with ${length} SKUs.`;
    }
    if (length === 2) {
      return `The SKUs "${firstSku}" and "${secondSku}" have been saved as a Variable Product.`;
    }
    if (length === 1) {
      return `The SKU "${firstSku}" has been saved as a Variable Product.`;
    }
    return `The SKUs ${joinedSkus} have been saved as a Variable Product.`;
  }
  if (length > 5) {
    return `Your product has been saved with ${length} SKUs.`;
  }
  if (length === 2) {
    return `The SKUs "${firstSku}" and "${secondSku}" have been saved.`;
  }
  if (length === 1) {
    return `The SKU "${firstSku}" has been saved.`;
  }
  return `The SKUs ${joinedSkus} have been saved.`;
};

export const formatAllData = (
  initialProductData,
  productFormData,
  productMetadata,
  metadataDirtyFields
) => {
  let submitData = {};
  let formattedProductData = {};
  let formattedMetadata = {};
  if (!_.isEqual(initialProductData, productFormData)) {
    formattedProductData = formatProductToSubmit(
      productFormData,
      initialProductData
    );
  }
  if (productMetadata)
    formattedMetadata = formatMetadata(metadataDirtyFields, productMetadata);
  if (!_.isEmpty(formattedMetadata) || !_.isEmpty(formattedProductData)) {
    submitData = {
      ...formattedProductData,
      //product id is needed to submit the metadata
      productId: productFormData.productId,
      variantMetadataMap: formattedMetadata,
    };
  }
  return submitData;
};

// validate required fields to enable/disable the private/public switch
export const getPublicSwitchEnabled = (productFormData) => {
  const {
    brandName,
    productName,
    taxonomyId,
    productCatalogVariants,
    defaultVariant,
  } = productFormData;
  // shared required product fields
  if (
    !brandName ||
    !productName ||
    !taxonomyId ||
    (_.size(productCatalogVariants) > 1 && !defaultVariant)
  )
    return false;
  // individual required variable fields
  const variantIndex = defaultVariant || 0;
  const retailPrice = _.get(productCatalogVariants, [
    variantIndex,
    'retailPrice',
  ]);
  const sku = _.get(productCatalogVariants, [variantIndex, 'sku']);
  const thumbnailImage = _.get(productCatalogVariants, [
    variantIndex,
    'imageUrl',
  ]);
  if (!sku || !retailPrice || retailPrice === '0' || !thumbnailImage)
    return false;
  return true;
};
// validate required fields by clicking the disabled switch and highlight the missing ones
export const validateRequiredPublicFields = (
  productFormData,
  handlePublicProductFieldsError,
  handlePublicVariantFieldsError
) => {
  const {
    brandName,
    productName,
    taxonomyId,
    productCatalogVariants,
    defaultVariant,
  } = productFormData;
  const errorMessage = ' ';
  // shared required product fields
  if (!brandName) handlePublicProductFieldsError('brandName', errorMessage);
  if (!productName) handlePublicProductFieldsError('productName', errorMessage);
  if (!taxonomyId) handlePublicProductFieldsError('taxonomyId', errorMessage);
  // individual required variable fields
  if (_.size(productCatalogVariants) > 1 && !defaultVariant)
    handlePublicProductFieldsError('defaultVariant', errorMessage);
  const variantIndex = defaultVariant || 0;
  const sku = _.get(productCatalogVariants, [variantIndex, 'sku']);
  const retailPrice = _.get(productCatalogVariants, [
    variantIndex,
    'retailPrice',
  ]);
  const thumbnailImage = _.get(productCatalogVariants, [
    variantIndex,
    'imageUrl',
  ]);
  if (!sku) handlePublicVariantFieldsError('sku', variantIndex, errorMessage);
  if (!retailPrice || retailPrice === '0')
    handlePublicVariantFieldsError('retailPrice', variantIndex, errorMessage);
  if (!thumbnailImage)
    handlePublicVariantFieldsError(
      'thumbnailImage',
      variantIndex,
      errorMessage
    );
};

export const imageValidation = (currentImage, context) =>
  new Promise((resolve) => {
    const imageName = context === 'gallery' ? 'It' : 'Image';
    let error;
    if (currentImage && currentImage.size > 500000) {
      error = {
        title: 'Image Upload Failed',
        subtitle: `${imageName} is over 500KB. Please reduce image weight and try again.`,
      };

      resolve(error);
    }
    const img = new Image();
    img.onload = () => {
      if (img.width < 800 || img.height < 800) {
        error = {
          title: 'Image Upload Failed',
          subtitle: `${imageName} must be at least 800 pixels wide and 800 pixels tall.`,
        };
      } else if (img.width !== img.height) {
        error = {
          title: 'Image Upload Failed',
          subtitle: `${imageName} must be a square.`,
        };
      }
      resolve(error);
    };
    img.src = currentImage && window.URL.createObjectURL(currentImage);
  });

const getAllProductImages = (imageSource) => {
  const productCatalogVariants = _.get(
    imageSource,
    'productCatalogVariants',
    []
  );
  return _.reduce(
    productCatalogVariants,
    (result, variant) => {
      if (variant) {
        const { privateImageUrls = [], galleryUrls = [], imageUrl } = variant;
        return [
          ...result,
          ...privateImageUrls,
          ...galleryUrls,
          imageUrl,
        ].filter(Boolean);
      }
      return result;
    },
    []
  );
};

export const getImagesToDelete = (images, imagesToCompare) => {
  const allImages = getAllProductImages(images);
  const allImagesToCompare = getAllProductImages(imagesToCompare);
  return allImages.filter((item) => !_.includes(allImagesToCompare, item));
};

export const formatDescriptor = (variant) => {
  const { colorAttributeDescriptor, wordAttributes = [] } = variant;
  const descriptor = _.filter(
    [colorAttributeDescriptor, ...wordAttributes],
    Boolean
  ).join(', ');
  return descriptor;
};
